import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { eur, eurShort } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DecimalInput } from "@/components/ui/decimal-input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { OptionSelect } from "@/components/ui/option-select";
import { COLORS, axisProps, ChartTooltip } from "@/components/chartUtils";
import { useIsPhone } from "@/lib/useMediaQuery";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend,
} from "recharts";
import { Info, ArrowUp, ArrowDown, Sliders } from "@phosphor-icons/react";
import { useAccountScope } from "@/context/AccountScopeContext";
import { withAccountScope } from "@/lib/accountScope";

const HORIZONS = [3, 6, 12, 24];
const SCENARIOS = ["conservative", "expected", "optimistic"];
const SCEN_COLOR = { conservative: COLORS.expense, expected: COLORS.forecast, optimistic: COLORS.income };
const SCEN_LABEL = { conservative: "Conservative", expected: "Expected", optimistic: "Optimistic" };

const defaultAssumptions = () => ({
  income_growth_pct: 0, expense_growth_pct: 0, salary_increase: 0,
  recurring_future_income: 0, recurring_future_expense: 0,
  one_time_income_amount: 0, one_time_income_month: 0,
  one_time_expense_amount: 0, one_time_expense_month: 0,
});

export default function Forecast() {
  const phone = useIsPhone();
  const { scope, selectedAccount, ready: accountsReady } = useAccountScope();
  const [base, setBase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState("expected");
  const [horizon, setHorizon] = useState(12);
  const [assumptions, setAssumptions] = useState({
    conservative: defaultAssumptions(),
    expected: defaultAssumptions(),
    optimistic: defaultAssumptions(),
  });
  const [projections, setProjections] = useState({ conservative: [], expected: [], optimistic: [] });
  const debounceRef = useRef({});

  useEffect(() => {
    if (!accountsReady) return undefined;
    let active = true;
    // A new scope starts from its own baseline, so assumptions tuned for the
    // previous account are dropped along with any recompute still pending.
    Object.values(debounceRef.current).forEach(clearTimeout);
    setLoading(true);
    (async () => {
      const res = await api.get(`/forecast?${withAccountScope({}, scope)}`);
      if (!active) return;
      setBase(res.data);
      const a = {}, p = {};
      SCENARIOS.forEach((s) => {
        a[s] = { ...defaultAssumptions(), ...res.data.scenarios[s].assumptions };
        p[s] = res.data.scenarios[s].projection;
      });
      setAssumptions(a);
      setProjections(p);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [scope, accountsReady]);

  const recompute = useCallback((scen, a) => {
    clearTimeout(debounceRef.current[scen]);
    debounceRef.current[scen] = setTimeout(async () => {
      if (!base) return;
      const res = await api.post("/forecast/project", {
        ...a,
        months: 24,
        base_monthly_income: base.base_monthly_income,
        base_monthly_expenses: base.base_monthly_expenses,
        current_balance: base.current_balance,
      });
      setProjections((prev) => ({ ...prev, [scen]: res.data.projection }));
    }, 300);
  }, [base]);

  const update = (scen, key, value) => {
    setAssumptions((prev) => {
      const next = { ...prev, [scen]: { ...prev[scen], [key]: value } };
      recompute(scen, next[scen]);
      return next;
    });
  };

  const a = assumptions[scenario];
  const proj = projections[scenario] || [];
  const sliced = proj.slice(0, horizon);
  const endBalance = sliced.length ? sliced[sliced.length - 1].balance : null;

  // Main chart: historical + active scenario projection
  const mainSeries = (() => {
    if (!base) return [];
    const hist = base.historical.map((h) => ({ label: h.label, historical: h.balance }));
    if (hist.length) hist[hist.length - 1].projected = base.historical[hist.length - 1].balance;
    const projected = sliced.map((p) => ({ label: p.label, projected: p.balance }));
    return [...hist, ...projected];
  })();
  const boundaryLabel = base?.historical?.length ? base.historical[base.historical.length - 1].label : null;

  // Comparison chart across scenarios
  const compareSeries = (() => {
    const len = horizon;
    const rows = [];
    for (let i = 0; i < len; i++) {
      const row = { label: projections.expected[i]?.label || `M${i + 1}` };
      SCENARIOS.forEach((s) => { row[s] = projections[s][i]?.balance; });
      rows.push(row);
    }
    return rows;
  })();

  // Month n of a one-time event is the n-th projected month, named as the
  // projection names it ("Mar 2027") rather than as an offset.
  const monthLabels = (projections.expected || []).map((p) => p.label);

  const horizonEnd = (scen) => {
    const p = projections[scen] || [];
    return p[horizon - 1]?.balance;
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tighter">Forecast</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {selectedAccount
              ? `Projections for ${selectedAccount.name} from its history and your assumptions.`
              : "Deterministic projections from your history and assumptions."}
          </p>
        </div>
        <div className="flex items-center gap-1 self-start rounded-md border border-border p-1 bg-card sm:gap-2">
          {HORIZONS.map((h) => (
            <button key={h} data-testid={`horizon-${h}`} onClick={() => setHorizon(h)}
              className={`min-w-11 px-3 py-2 text-sm rounded font-medium transition-colors sm:py-1.5 ${
                horizon === h ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}>
              {h}mo
            </button>
          ))}
        </div>
      </div>

      {/* Projection disclaimer */}
      <div className="flex items-start gap-2 text-xs text-muted-foreground border border-border rounded-md p-3 bg-card">
        <Info size={15} className="text-forecast shrink-0 mt-0.5" />
        <p>Projections are estimates based on your historical averages, recent trends and the assumptions below. They are <span className="text-foreground">not guaranteed outcomes</span>.</p>
      </div>

      {loading || !base ? (
        <div className="space-y-4">
          <Skeleton className="h-40 bg-card" /><Skeleton className="h-96 bg-card" />
        </div>
      ) : (
        <>
          <Tabs value={scenario} onValueChange={setScenario}>
            <TabsList className="bg-card border border-border" data-testid="scenario-tabs">
              {SCENARIOS.map((s) => (
                <TabsTrigger key={s} value={s} data-testid={`scenario-${s}`}
                  className="data-[state=active]:bg-secondary">{SCEN_LABEL[s]}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="forecast-current-balance">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Current Balance</p>
              <p className="font-heading font-extrabold text-2xl mt-2 tabular-nums">{eur(base.current_balance)}</p>
            </Card>
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="forecast-end-balance">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Projected · {horizon}mo</p>
              <p className="font-heading font-extrabold text-2xl mt-2 tabular-nums" style={{ color: SCEN_COLOR[scenario] }}>
                {endBalance != null ? eur(endBalance) : "—"}
              </p>
            </Card>
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Base Income /mo</p>
              <p className="font-heading font-extrabold text-2xl mt-2 tabular-nums text-income">{eur(base.base_monthly_income)}</p>
            </Card>
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Base Expenses /mo</p>
              <p className="font-heading font-extrabold text-2xl mt-2 tabular-nums text-expense">{eur(base.base_monthly_expenses)}</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main chart */}
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg lg:col-span-2" data-testid="forecast-main-chart">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold tracking-tight">{SCEN_LABEL[scenario]} · Balance Projection</h3>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-white" />Historical</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: SCEN_COLOR[scenario] }} />Projected</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={phone ? 230 : 340}>
                <AreaChart data={mainSeries}>
                  <defs>
                    <linearGradient id="fHist" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fff" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#fff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fProj" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={SCEN_COLOR[scenario]} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={SCEN_COLOR[scenario]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={COLORS.grid} />
                  <XAxis dataKey="label" {...axisProps} minTickGap={phone ? 40 : 20} />
                  <YAxis {...axisProps} tickFormatter={eurShort} width={phone ? 40 : 52} />
                  <Tooltip content={<ChartTooltip />} />
                  {boundaryLabel && <ReferenceLine x={boundaryLabel} stroke={COLORS.axis} strokeDasharray="3 3"
                    label={{ value: "Now", fill: COLORS.axis, fontSize: 10, position: "insideTopRight" }} />}
                  <Area dataKey="historical" name="Historical" stroke="#fff" strokeWidth={2} fill="url(#fHist)" connectNulls dot={false} />
                  <Area dataKey="projected" name="Projected" stroke={SCEN_COLOR[scenario]} strokeWidth={2}
                    strokeDasharray="5 4" fill="url(#fProj)" connectNulls dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Assumptions */}
            <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="assumptions-panel">
              <h3 className="font-heading font-bold tracking-tight mb-4 flex items-center gap-2">
                <Sliders size={17} /> Assumptions
              </h3>
              <div className="space-y-5">
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-xs text-muted-foreground">Monthly income growth</Label>
                    <span className="text-xs tabular-nums font-semibold">{a.income_growth_pct.toFixed(1)}%</span>
                  </div>
                  <Slider data-testid="slider-income-growth" min={-10} max={10} step={0.1}
                    value={[a.income_growth_pct]} onValueChange={([v]) => update(scenario, "income_growth_pct", v)} />
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <Label className="text-xs text-muted-foreground">Monthly expense growth</Label>
                    <span className="text-xs tabular-nums font-semibold">{a.expense_growth_pct.toFixed(1)}%</span>
                  </div>
                  <Slider data-testid="slider-expense-growth" min={-10} max={10} step={0.1}
                    value={[a.expense_growth_pct]} onValueChange={([v]) => update(scenario, "expense_growth_pct", v)} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Salary increase (€/mo)</Label>
                  <EuroInput testid="input-salary-increase" allowNegative
                    value={a.salary_increase} onChange={(v) => update(scenario, "salary_increase", v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Recurring income (€/mo)</Label>
                    <EuroInput testid="input-recurring-income"
                      value={a.recurring_future_income} onChange={(v) => update(scenario, "recurring_future_income", v)} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Recurring expense (€/mo)</Label>
                    <EuroInput testid="input-recurring-expense"
                      value={a.recurring_future_expense} onChange={(v) => update(scenario, "recurring_future_expense", v)} />
                  </div>
                </div>
                <div className="border-t border-border pt-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">One-time events</p>
                  <OneTime label="Income" color="text-income"
                    amount={a.one_time_income_amount} month={a.one_time_income_month}
                    onAmount={(v) => update(scenario, "one_time_income_amount", v)}
                    onMonth={(v) => update(scenario, "one_time_income_month", v)}
                    monthLabels={monthLabels}
                    testid="onetime-income" />
                  <OneTime label="Expense" color="text-expense"
                    amount={a.one_time_expense_amount} month={a.one_time_expense_month}
                    onAmount={(v) => update(scenario, "one_time_expense_amount", v)}
                    onMonth={(v) => update(scenario, "one_time_expense_month", v)}
                    monthLabels={monthLabels}
                    testid="onetime-expense" />
                </div>
                <Button variant="outline" className="w-full border-border" data-testid="reset-assumptions"
                  onClick={() => {
                    const reset = { ...defaultAssumptions(), ...base.scenarios[scenario].assumptions };
                    setAssumptions((prev) => ({ ...prev, [scenario]: reset }));
                    recompute(scenario, reset);
                  }}>
                  Reset to preset
                </Button>
              </div>
            </Card>
          </div>

          {/* Scenario comparison */}
          <Card className="bg-card border-border p-4 sm:p-5 rounded-lg" data-testid="scenario-comparison-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold tracking-tight">Scenario Comparison · {horizon} months</h3>
            </div>
            <ResponsiveContainer width="100%" height={phone ? 210 : 280}>
              <LineChart data={compareSeries}>
                <CartesianGrid vertical={false} stroke={COLORS.grid} />
                <XAxis dataKey="label" {...axisProps} minTickGap={phone ? 40 : 20} />
                <YAxis {...axisProps} tickFormatter={eurShort} width={phone ? 40 : 52} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => SCEN_LABEL[v]} />
                {SCENARIOS.map((s) => (
                  <Line key={s} dataKey={s} name={s} stroke={SCEN_COLOR[s]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
              {SCENARIOS.map((s) => (
                <div key={s} className="flex items-center justify-between gap-2 rounded-md border border-border p-3 sm:block" data-testid={`compare-end-${s}`}>
                  <p className="text-xs" style={{ color: SCEN_COLOR[s] }}>{SCEN_LABEL[s]}</p>
                  <p className="tabular-nums font-heading font-bold text-base sm:mt-1 sm:text-lg">
                    {horizonEnd(s) != null ? eur(horizonEnd(s)) : "—"}
                  </p>
                </div>
              ))}
            </div>
          </Card>

          {/* Monthly projection table */}
          <Card className="bg-card border-border rounded-lg overflow-hidden" data-testid="projection-table">
            <div className="p-5 border-b border-border">
              <h3 className="font-heading font-bold tracking-tight">{SCEN_LABEL[scenario]} · Monthly Projection</h3>
            </div>
            <div className="overflow-x-auto max-h-[360px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-3 py-3 sm:px-5">Month</th>
                    <th className="hidden text-right px-3 py-3 sm:table-cell sm:px-5">Income</th>
                    <th className="hidden text-right px-3 py-3 sm:table-cell sm:px-5">Expense</th>
                    <th className="text-right px-3 py-3 sm:px-5">Net</th>
                    <th className="text-right px-3 py-3 sm:px-5">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {sliced.map((p) => (
                    <tr key={p.month_index} className="border-b border-border/60 tabular-nums">
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap sm:px-5">{p.label}</td>
                      <td className="hidden px-3 py-2.5 text-right text-income sm:table-cell sm:px-5">{eur(p.income)}</td>
                      <td className="hidden px-3 py-2.5 text-right text-expense sm:table-cell sm:px-5">{eur(p.expense)}</td>
                      <td className={`px-3 py-2.5 text-right sm:px-5 ${p.net >= 0 ? "text-income" : "text-expense"}`}>{eur(p.net)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold sm:px-5">{eur(p.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * A euro amount the user can clear and retype. An empty field counts as 0,
 * which is what an assumption left blank means to the projection.
 */
function EuroInput({ testid, value, onChange, allowNegative = false }) {
  return (
    <DecimalInput
      data-testid={testid}
      value={value}
      onValueChange={onChange}
      emptyValue={0}
      allowNegative={allowNegative}
      enterKeyHint="done"
      className="mt-1 bg-background tabular-nums touch:h-11"
    />
  );
}

function monthOptions(monthLabels) {
  const months = Array.from({ length: 24 }, (_, i) => ({
    value: String(i + 1),
    label: monthLabels[i] || `Month ${i + 1}`,
  }));
  return [{ value: "0", label: "None" }, ...months];
}

function OneTime({ label, color, amount, month, onAmount, onMonth, monthLabels, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2 items-end">
      <div>
        <Label className={`text-xs ${color}`}>{label} (€)</Label>
        <EuroInput testid={`${testid}-amount`} value={amount} onChange={onAmount} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">In month</Label>
        <OptionSelect
          title={`${label} in month`}
          options={monthOptions(monthLabels)}
          value={String(month)}
          onValueChange={(v) => onMonth(Number(v))}
          data-testid={`${testid}-month`}
          className="mt-1 bg-background"
        />
      </div>
    </div>
  );
}
